import { useState, useRef } from 'react'
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useWhatsAppMailboxes } from '@hooks/useWhatsAppMailboxes'
import { useCreateWaContact } from '@hooks/useWaContacts'
import { toast } from 'react-hot-toast'

interface ImportWaContactsDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ParsedContact {
  phoneNumber: string
  name?: string
  email?: string
  company?: string
  notes?: string
  isValid: boolean
  error?: string
}

export function ImportWaContactsDialog({ isOpen, onClose, onSuccess }: ImportWaContactsDialogProps) {
  const [accountId, setAccountId] = useState<number | null>(null)
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: mailboxesData } = useWhatsAppMailboxes()
  const mailboxes = mailboxesData?.data || []
  const createContact = useCreateWaContact()

  const handleClose = () => {
    setParsedContacts([])
    setAccountId(null)
    setImportProgress({ current: 0, total: 0 })
    onClose()
  }

  const parseCSV = (content: string): ParsedContact[] => {
    const lines = content.split('\n').filter((line) => line.trim())
    if (lines.length < 2) return []

    const headers = lines[0].toLowerCase().split(',').map((h) => h.trim())
    const phoneIndex = headers.findIndex((h) =>
      h.includes('phone') || h.includes('number') || h.includes('tel') || h.includes('mobile')
    )
    const nameIndex = headers.findIndex((h) => h.includes('name'))
    const emailIndex = headers.findIndex((h) => h.includes('email') || h.includes('mail'))
    const companyIndex = headers.findIndex((h) =>
      h.includes('company') || h.includes('organization') || h.includes('org')
    )
    const notesIndex = headers.findIndex((h) => h.includes('note') || h.includes('comment'))

    if (phoneIndex === -1) {
      toast.error('CSV must have a phone/number column')
      return []
    }

    const contacts: ParsedContact[] = []
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''))

      const phoneNumber = values[phoneIndex]?.replace(/[\s\-\(\)]/g, '') || ''
      const phoneRegex = /^\+?[1-9]\d{1,14}$/
      const isValid = phoneRegex.test(phoneNumber)

      contacts.push({
        phoneNumber,
        name: nameIndex >= 0 ? values[nameIndex] : undefined,
        email: emailIndex >= 0 ? values[emailIndex] : undefined,
        company: companyIndex >= 0 ? values[companyIndex] : undefined,
        notes: notesIndex >= 0 ? values[notesIndex] : undefined,
        isValid,
        error: isValid ? undefined : 'Invalid phone number format',
      })
    }

    return contacts
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      const parsed = parseCSV(content)
      setParsedContacts(parsed)

      const validCount = parsed.filter((c) => c.isValid).length
      toast.success(`Parsed ${validCount} valid contacts from ${parsed.length} rows`)
    }
    reader.readAsText(file)
  }

  const handleImport = async () => {
    if (!accountId) {
      toast.error('Please select a WhatsApp account')
      return
    }

    const validContacts = parsedContacts.filter((c) => c.isValid)
    if (validContacts.length === 0) {
      toast.error('No valid contacts to import')
      return
    }

    setIsImporting(true)
    setImportProgress({ current: 0, total: validContacts.length })

    let successCount = 0
    let errorCount = 0

    for (let i = 0; i < validContacts.length; i++) {
      const contact = validContacts[i]
      try {
        await createContact.mutateAsync({
          phone: contact.phoneNumber,
          name: contact.name || undefined,
          notes: contact.notes || undefined,
        })
        successCount++
      } catch (error) {
        errorCount++
      }
      setImportProgress({ current: i + 1, total: validContacts.length })
    }

    setIsImporting(false)

    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} contacts`)
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} contacts`)
      }
      handleClose()
      onSuccess?.()
    } else {
      toast.error('Failed to import contacts')
    }
  }

  if (!isOpen) return null

  const validCount = parsedContacts.filter((c) => c.isValid).length
  const invalidCount = parsedContacts.length - validCount

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Contacts from CSV</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isImporting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* WhatsApp Account Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              WhatsApp Account <span className="text-red-500">*</span>
            </label>
            <select
              value={accountId || ''}
              onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={isImporting}
            >
              <option value="">Select an account...</option>
              {mailboxes
                ?.filter((mb) => mb.isConnected)
                .map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.name} ({mailbox.phoneNumber})
                  </option>
                ))}
            </select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">CSV File</label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isImporting}
              />
              <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 mb-1">Click to upload CSV file</p>
              <p className="text-xs text-gray-500">
                Required column: phone/number. Optional: name, email, company, notes
              </p>
            </div>
          </div>

          {/* CSV Format Help */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format Example</h4>
            <code className="text-xs text-blue-700 block bg-blue-100 p-2 rounded">
              phone,name,email,company<br />
              +1234567890,John Doe,john@example.com,Acme Inc<br />
              +0987654321,Jane Smith,jane@example.com,
            </code>
          </div>

          {/* Parsed Results */}
          {parsedContacts.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">Parsed Contacts</h4>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center text-green-600">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    {validCount} valid
                  </span>
                  {invalidCount > 0 && (
                    <span className="flex items-center text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {invalidCount} invalid
                    </span>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Phone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedContacts.slice(0, 100).map((contact, index) => (
                      <tr key={index} className={contact.isValid ? '' : 'bg-red-50'}>
                        <td className="px-3 py-2">
                          {contact.isValid ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{contact.phoneNumber}</td>
                        <td className="px-3 py-2">{contact.name || '-'}</td>
                        <td className="px-3 py-2">{contact.email || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedContacts.length > 100 && (
                  <div className="px-3 py-2 text-center text-sm text-gray-500 bg-gray-50">
                    Showing first 100 of {parsedContacts.length} contacts
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Importing contacts...</span>
                <span className="text-gray-900 font-medium">
                  {importProgress.current} / {importProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={isImporting}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={isImporting || validCount === 0 || !accountId}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            <span>{isImporting ? 'Importing...' : `Import ${validCount} Contacts`}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
